// NyxCatalog.js
import React from 'react';
import './NyxCatalog.css';
import './NYXNoCode.css';
import MenaGallery2024To2025 from './MenaGallery2024_2025';
import Seo from "./components/Seo";
const NyxCatalog = () => {
    return (
        <div className="nyx-catalog-page">
            <Seo
                title="NYX Catalog — Tools, Templates & Prompts"
                description="Browse ready-to-use tools, templates, and prompt packs that help students build with AI instantly."
            />
            <header className="nyx-catalog-header">
                <h1>NYX NoCode Hackathon</h1>
            </header>

            <section className="nyx-catalog-section">
                <h2>Student Creativity in Action</h2>
                <p><strong>What happens when every student can turn an idea into an app, a game, or an interactive
                    story—without needing to learn complex code first?</strong> <em>NYX NoCode makes that leap possible.</em></p>

                <p>In Mena, Arkansas, we saw classrooms transform into studios where students designed projects that
                    were playful, purposeful, and powerful.
                    From middle school beginners to high school innovators, every student team had the tools to
                    contribute and the confidence to share their vision.</p>

                <div className="modes-grid">
                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">👩‍🎓</div>
                        <div className="mode-content">
                            <h3>See Real Projects</h3>
                            <p>Explore student work from the Mena School District hackathon.</p>
                        </div>
                    </div>

                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">🎙️</div>
                        <div className="mode-content">
                            <h3>Hear Their Stories</h3>
                            <p>Listen to NPR broadcasts capturing the energy of the event.</p>
                        </div>
                    </div>

                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">⚖️</div>
                        <div className="mode-content">
                            <h3>Understand the Framework</h3>
                            <p>See how NYX NoCode levels the playing field for all learners.</p>
                        </div>
                    </div>

                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">🚀</div>
                        <div className="mode-content">
                            <h3>Bring It to Your School</h3>
                            <p>Choose from Demo Days to full Hacktopia experiences.</p>
                        </div>
                    </div>
                </div>

                <p className="glance-next">The future of software is generative. With NYX NoCode, your students
                    don't just learn about it—they live it.</p>
            </section>

            <section className="nyx-catalog-section">
                <h2>Mena School District NYX NoCode Hackathon Gallery</h2>
                <p>
                    The students of Mena High School and Mena Middle School participated in the NYX NoCode Hackathon in
                    November, 2024. Eight students were awarded with mountain bikes and mountain bike lessons, along
                    with dozens of other prizes such as books and AI educational materials. KUAF-FM's Ozarks at Large
                    did two reports on this.
                </p>
                <div className="radio-links">
                    <a href={"https://www.kuaf.com/show/ozarks-at-large/2024-11-26/nyx-no-code-enables-mena-students-to-create-using-ai"}
                       className="radio-button" target="_blank" rel="noopener noreferrer">
                        <span className="radio-icon">🔊</span>
                        <span className="radio-text">
                            <strong>Listen to NPR Radio Broadcast</strong><br/>
                            KUAF-FM Ozarks at Large: November 2024<br/>
                            NYX NoCode Enables Mena Students to Create using AI
                        </span>
                    </a>
                    <a href={"https://www.kuaf.com/show/ozarks-at-large/2025-04-22/creating-the-unexpected-with-ai-during-menas-no-code-hackathon"}
                       className="radio-button" target="_blank" rel="noopener noreferrer">
                        <span className="radio-icon">🔊</span>
                        <span className="radio-text">
                            <strong>Listen to NPR Radio Broadcast</strong><br/>
                            KUAF-FM Ozarks at Large: April 2025<br/>
                            Creating the Unexpected with AI during Mena's NoCode Hackathon
                        </span>
                    </a>
                </div>
                <p>Each of these 2024-2025 school year entries was created by a student team in middle school or high school in computer science class, with the goal of promoting The Trails at Mena, the city's new mountain bike park.</p>
                <MenaGallery2024To2025 />
            </section>

            <section className="nyx-catalog-section">
                <div className="catalog-highlight">
                    <h2>How It Works</h2>
                    <ul>
                        <li><strong>Our team is virtual</strong> — we provide the platform, framework, and support</li>
                        <li><strong>Held in class</strong> — teachers organize students into small teams</li>
                        <li><strong>Teachers lead hands-on</strong> — guiding, learning, and creating alongside the kids</li>
                        <li><strong>Kickoff</strong> — meet Justin Mascot</li>
                        <li><strong>Workshops</strong> — fast, engaging intros to no-code tools</li>
                        <li><strong>Challenges</strong> — teams design apps to solve real or imaginative problems</li>
                        <li><strong>Showcase</strong> — a fun celebration of student work</li>
                    </ul>
                </div>
            </section>

            <section className="nyx-catalog-section">
                <div className="catalog-highlight">
                    <h2>Leveling the Playing Field</h2>
                    <p>
                        Traditional programming is something only a few students excel at. For everyone else, the barriers—syntax, debugging, complexity—keep them from fully participating.
                    </p>
                    <p>
                        <strong>NYX NoCode removes those barriers.</strong>
                    </p>
                    <p>
                        Now every student, regardless of background or skill level, can take part in building apps, solving problems, and expressing creativity with technology.
                    </p>
                </div>
            </section>

            <section className="nyx-catalog-section">
                <h2>Teachers as Co-Learners and Guides</h2>
                <p>
                    NYX NoCode is built on a teacher-centric model that makes AI creation approachable and empowering. Educators don't need to be tech experts—NYX makes it simple to guide students through AI-powered creation. Teachers engage as co-learners, exploring alongside their students, modeling curiosity, and building their own digital fluency in real time.
                </p>

                <div className="modes-grid">
                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">👩‍🏫</div>
                        <div className="mode-content">
                            <h3>Teacher-Led, Classroom-Ready</h3>
                            <p>Teacher-led learning keeps AI safe, structured, and classroom-ready.</p>
                        </div>
                    </div>

                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">📚</div>
                        <div className="mode-content">
                            <h3>Built-in Support System</h3>
                            <p>Help documentation and guides are stored in the Launch Pad with an AI chat advisor for instant assistance.</p>
                        </div>
                    </div>

                    <div className="mode-section">
                        <div className="mode-icon" aria-hidden="true">🔧</div>
                        <div className="mode-content">
                            <h3>Perfect for Tech-Savvy Educators</h3>
                            <p>Experienced educators can leverage their technical expertise to help students reverse-engineer their AI creations, diving deep into how and why their apps work.</p>
                        </div>
                    </div>
                </div>

                <p>
                    NYX NoCode supports educators at every step, enabling classrooms to embrace project-based learning with AI in ways that are ethical, fun, and transformative.
                </p>

                <p className="glance-next">
                    <strong>Want to build your AI fluency first?</strong> Explore our <a href="/empower">Teacher Training program</a> — hands-on professional development that empowers educators to teach with confidence, creativity, and curiosity.
                </p>
            </section>

            <section className="nyx-catalog-section">
                <h2>Program Options</h2>
                
                <div className="program-option">
                    <h3>Demo Day — $500</h3>
                    <ul>
                        <li>100 Team IDs issued to teachers, who form student teams</li>
                        <li>One-day classroom event</li>
                        <li>Introduction to AI + prompt exploration</li>
                        <li>Teachers lead, students create, Justin Mascot guides</li>
                        <li>Fast, fun, and accessible</li>
                    </ul>
                </div>

                <div className="program-option">
                    <h3>Hacktopia/3-week NYX NoCode Hackathon — $5,000</h3>
                    <ul>
                        <li>A 21-day classroom experience</li>
                        <li>100 Team IDs issued to teachers, who form student teams</li>

                        <li>Teams build apps and projects with NYX NoCode</li>
                        <li>Entries judged + student certificates awarded</li>
                        <li>Option to organize community prizes for added excitement</li>
                        <li>Engaging, structured, and transformative</li>
                    </ul>
                </div>

                <div className="program-option">
                    <h3>Extended Access</h3>
                    <p>
                        Want more? Special arrangements are available for schools that wish to integrate NYX NoCode beyond a single event, bringing long-term no-code AI creation into the classroom.
                    </p>
                </div>
            </section>

        </div>
    );
};

export default NyxCatalog;
